import type { RawDealItemForValidation } from '@/lib/workers/validation.worker';
import { convertDateFields } from '@/lib/apollo-client';
import { getDealsApiBaseUrl } from '@/lib/config';
import { DealItemWithVersions } from '@/lib/graphql/generated/graphql';
import {
  mergeStreamedItem,
  normalize,
  toRawValidationItem,
} from '@/lib/streaming/normalize-deal-items';
import {
  type DealItemsBatch,
  DealItemsStreamError,
  streamDealItems,
} from '@/lib/streaming/stream-deal-items';
import {
  markBackendConfirmed,
  resetValidationWorker,
  validate,
} from '@/lib/validation/validation-service';
import { dealItemDescriptionsByActiveVersionIdState } from '@/states/deal-item-descriptions.state';
import { pendingOptimisticUpdatesState } from '@/states/pending-optimistic-updates.state';
import {
  backendValidatedProductIdsAtom,
  dealItemsLoadingState,
  dealItemsStreamingState,
  localValidationErrorsAtom,
  productsMapState,
} from '@/states/products.state';
import { getDefaultStore } from 'jotai';

const store = getDefaultStore();

const STREAM_MAX_RETRIES = 2;
const STREAM_RETRY_DELAY_MS = 300;

const delay = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

export type DealItemsStreamResult = 'success' | 'error' | 'aborted';

type ActiveStream = {
  dealId: string;
  controller: AbortController;
  promise: Promise<DealItemsStreamResult>;
  queuedRefresh: Promise<DealItemsStreamResult> | null;
};

let active: ActiveStream | null = null;
let completedDealId: string | null = null;

type EnsureArgs = {
  dealId: string;
  isRefresh?: boolean;
  getToken: () => Promise<string>;
};

const isNonRetryableStreamError = (caught: unknown): boolean =>
  caught instanceof DealItemsStreamError &&
  caught.status !== undefined &&
  caught.status >= 400 &&
  caught.status < 500;

export function ensureDealItemsStream({
  dealId,
  isRefresh = false,
  getToken,
}: EnsureArgs): Promise<DealItemsStreamResult> {
  if (!dealId) return Promise.resolve('aborted');

  if (active && active.dealId === dealId) {
    if (!isRefresh) return active.promise;
    if (!active.queuedRefresh) {
      const { controller } = active;
      active.queuedRefresh = active.promise.then((result) => {
        if (controller.signal.aborted) return 'aborted';
        if (result === 'aborted') return result;
        return ensureDealItemsStream({ dealId, isRefresh: true, getToken });
      });
    }
    return active.queuedRefresh;
  }

  if (!isRefresh && completedDealId === dealId) {
    isRefresh = true;
  }

  if (active) {
    active.controller.abort();
    active = null;
  }

  const controller = new AbortController();
  const promise = runStream({ dealId, isRefresh, getToken, controller });
  active = { dealId, controller, promise, queuedRefresh: null };
  return promise;
}

/** Aborts the in-flight stream for this deal (useEffect cleanup on unmount). */
export function stopDealItemsStream(dealId: string): void {
  if (active && active.dealId === dealId) {
    active.controller.abort();
    active = null;
  }
}

async function runStream({
  dealId,
  isRefresh,
  getToken,
  controller,
}: Required<Omit<EnsureArgs, 'isRefresh'>> & {
  isRefresh: boolean;
  controller: AbortController;
}): Promise<DealItemsStreamResult> {
  const existingIdsAtStart = isRefresh
    ? new Set(store.get(productsMapState).keys())
    : new Set<string>();
  const buffered = isRefresh;

  store.set(dealItemsStreamingState, existingIdsAtStart.size === 0);

  if (!isRefresh) {
    completedDealId = null;
    resetValidationWorker();
    store.set(productsMapState, new Map());
    store.set(dealItemDescriptionsByActiveVersionIdState, null);
    store.set(backendValidatedProductIdsAtom, new Set());
    store.set(localValidationErrorsAtom, null);
    store.set(dealItemsLoadingState, {
      initialLoading: true,
      error: undefined,
    });
  }

  let firstBatchReceived = false;
  let validationItems: RawDealItemForValidation[] = [];
  let streamedIds = new Set<string>();
  let bufferedItems: DealItemWithVersions[] = [];
  const idsFromFailedAttempts = new Set<string>();

  const applyToProductsMap = (
    normalizedItems: DealItemWithVersions[]
  ): void => {
    const pendingIds = store.get(pendingOptimisticUpdatesState);
    store.set(productsMapState, (prev) => {
      const next = new Map(prev);
      for (const n of normalizedItems) {
        next.set(n.id, mergeStreamedItem(n, next.get(n.id), pendingIds));
      }
      return next;
    });
  };

  const attemptStream = async (): Promise<void> => {
    for (const id of streamedIds) idsFromFailedAttempts.add(id);
    validationItems = [];
    streamedIds = new Set<string>();
    bufferedItems = [];

    const onBatch = (batch: DealItemsBatch): void => {
      if (controller.signal.aborted) return;
      convertDateFields(batch);
      for (const item of batch.items) streamedIds.add(item.id);
      validationItems.push(...batch.items.map(toRawValidationItem));

      const normalizedItems = normalize(batch.items);
      if (buffered) {
        bufferedItems.push(...normalizedItems);
        return;
      }

      applyToProductsMap(normalizedItems);
      markBackendConfirmed(batch.items.map((item) => item.id));
      validate(validationItems);

      if (!firstBatchReceived) {
        firstBatchReceived = true;
        store.set(dealItemsLoadingState, {
          initialLoading: false,
          error: undefined,
        });
      }
    };

    const token = await getToken();
    if (controller.signal.aborted) return;

    await streamDealItems({
      baseUrl: getDealsApiBaseUrl(),
      dealId,
      token,
      signal: controller.signal,
      onBatch,
    });
  };

  try {
    for (let attempt = 0; ; attempt++) {
      try {
        await attemptStream();
        break;
      } catch (caught) {
        if (
          controller.signal.aborted ||
          attempt >= STREAM_MAX_RETRIES ||
          isNonRetryableStreamError(caught)
        ) {
          throw caught;
        }
        await delay(STREAM_RETRY_DELAY_MS * (attempt + 1));
        if (controller.signal.aborted) return 'aborted';
      }
    }

    if (controller.signal.aborted) return 'aborted';

    if (buffered) {
      applyToProductsMap(bufferedItems);
      markBackendConfirmed(Array.from(streamedIds));
    }

    if (isRefresh) {
      store.set(productsMapState, (prev) => {
        const next = new Map<string, DealItemWithVersions>();
        for (const [id, item] of prev) {
          if (streamedIds.has(id) || !existingIdsAtStart.has(id)) {
            next.set(id, item);
          }
        }
        return next;
      });
    } else if (idsFromFailedAttempts.size > 0) {
      store.set(productsMapState, (prev) => {
        const next = new Map(prev);
        for (const id of idsFromFailedAttempts) {
          if (!streamedIds.has(id)) next.delete(id);
        }
        return next;
      });
    }

    validate(validationItems);
    store.set(dealItemsLoadingState, {
      initialLoading: false,
      error: undefined,
    });
    store.set(dealItemsStreamingState, false);
    completedDealId = dealId;
    return 'success';
  } catch (caught) {
    if (controller.signal.aborted) return 'aborted';
    store.set(dealItemsStreamingState, false);
    if (!isRefresh || store.get(productsMapState).size === 0) {
      const error =
        caught instanceof Error ? caught : new Error(String(caught));
      store.set(dealItemsLoadingState, { initialLoading: false, error });
    }
    return 'error';
  } finally {
    const ownsGlobalState = active === null || active.controller === controller;
    if (active && active.controller === controller) {
      active = null;
    }
    if (controller.signal.aborted && ownsGlobalState) {
      store.set(dealItemsStreamingState, false);
    }
  }
}

export function __resetDealItemsStreamControllerForTests(): void {
  if (active) active.controller.abort();
  active = null;
  completedDealId = null;
}
